import type { DirectSource } from '@camino-solutions/share/proto/projects/platform/messages';
import type { DimensionValueFallbackSelectorNode } from '../typing/associate-header';

export type DimensionValueFallbackSelectorConfig = Record<
  string,
  DimensionValueFallbackSelectorNode[]
>;

export type DirectSourceWithDimensionConfig = DirectSource.AsObject & {
  dimensionValueFallbackSelectors?: DimensionValueFallbackSelectorConfig;
};

const registry = new Map<string, DimensionValueFallbackSelectorConfig>();

export function registerDimensionValueFallbackSelectorConfig(
  sourceId: string,
  config: DimensionValueFallbackSelectorConfig,
): void {
  registry.set(sourceId, cloneDimensionValueFallbackSelectorConfig(config));
}

export function getDimensionValueFallbackSelectorConfig(
  sourceId: string,
): DimensionValueFallbackSelectorConfig | undefined {
  const config = registry.get(sourceId);
  return config ? cloneDimensionValueFallbackSelectorConfig(config) : undefined;
}

export function clearDimensionValueFallbackSelectorConfig(sourceId?: string): void {
  if (sourceId !== undefined) {
    registry.delete(sourceId);
    return;
  }

  registry.clear();
}

export function attachDimensionValueFallbackSelectorConfig(
  source: DirectSource,
): DirectSourceWithDimensionConfig {
  const sourceObject = source.toObject() as DirectSourceWithDimensionConfig;

  if (!source.sourceId) {
    return sourceObject;
  }

  const config = getDimensionValueFallbackSelectorConfig(source.sourceId);
  if (config !== undefined) {
    sourceObject.dimensionValueFallbackSelectors = config;
  } else {
    delete sourceObject.dimensionValueFallbackSelectors;
  }

  return sourceObject;
}

export function cloneDimensionValueFallbackSelectorConfig(
  config: DimensionValueFallbackSelectorConfig,
): DimensionValueFallbackSelectorConfig {
  return Object.entries(config).reduce<DimensionValueFallbackSelectorConfig>(
    (acc, [field, tree]) => {
      acc[field] = cloneDimensionValueFallbackSelectorNodes(tree);
      return acc;
    },
    {},
  );
}

export function cloneDimensionValueFallbackSelectorNodes(
  nodes: DimensionValueFallbackSelectorNode[],
  depth = 0,
): DimensionValueFallbackSelectorNode[] {
  return nodes.map(node => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const clonedNode: DimensionValueFallbackSelectorNode = {
      key: node.key,
      label: node.label,
      ...(node.selectable !== undefined ? { selectable: node.selectable } : {}),
      ...(node.hasChildren !== undefined ? { hasChildren: node.hasChildren } : {}),
      ...(node.dataSource !== undefined ? { dataSource: node.dataSource } : {}),
      ...(node.expanded !== undefined
        ? { expanded: node.expanded }
        : depth === 0 && hasChildren
          ? { expanded: true }
          : {}),
      ...(node.attributes ? { attributes: { ...node.attributes } } : {}),
    };

    if (hasChildren) {
      clonedNode.children = cloneDimensionValueFallbackSelectorNodes(node.children!, depth + 1);
    }

    return clonedNode;
  });
}
